import { Link, useNavigate } from "react-router-dom";
import { PageRow } from "../lib/api";

interface TreeNode {
  segment: string;
  slug?: string;
  title?: string;
  hasDraft?: boolean;
  children: TreeNode[];
}

function buildTree(pages: PageRow[]): TreeNode[] {
  const root: TreeNode[] = [];
  for (const page of pages) {
    const parts = page.slug.split("/");
    let nodes = root;
    for (let i = 0; i < parts.length; i++) {
      const segment = parts[i]!;
      let node = nodes.find((n) => n.segment === segment);
      if (!node) {
        node = { segment, children: [] };
        nodes.push(node);
      }
      if (i === parts.length - 1) {
        node.slug = page.slug;
        node.title = page.title;
        node.hasDraft = !!page.has_draft;
      }
      nodes = node.children;
    }
  }
  return root;
}

function TreeNodes({
  nodes,
  username,
  spaceSlug,
  currentSlug,
  depth,
}: {
  nodes: TreeNode[];
  username: string;
  spaceSlug: string;
  currentSlug?: string;
  depth: number;
}) {
  const pl = 10 + depth * 12;
  const navigate = useNavigate();
  return (
    <>
      {nodes.map((node) => (
        <div key={node.segment}>
          {node.slug ? (
            <Link
              to={`/${username}/${spaceSlug}/${node.slug}`}
              style={{ paddingLeft: `${pl}px`, paddingRight: "10px" }}
              className={`flex items-center gap-2 py-2 rounded-md text-sm transition-colors ${
                node.slug === currentSlug
                  ? "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800"
              }`}
            >
              <span className="text-xs shrink-0 text-gray-400 dark:text-gray-600">📄</span>
              <span className="truncate flex-1">{node.title ?? node.segment}</span>
              {node.hasDraft && (
                <span
                  className="shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500"
                  title="Has pending draft — click to review"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/${username}/${spaceSlug}/${node.slug}/edit`);
                  }}
                />
              )}
            </Link>
          ) : (
            <div
              style={{ paddingLeft: `${pl}px` }}
              className="flex items-center gap-1.5 py-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
            >
              <span>▸</span>
              <span className="truncate">{node.segment}</span>
            </div>
          )}
          {node.children.length > 0 && (
            <TreeNodes
              nodes={node.children}
              username={username}
              spaceSlug={spaceSlug}
              currentSlug={currentSlug}
              depth={depth + 1}
            />
          )}
        </div>
      ))}
    </>
  );
}

export default function SidebarPageList({
  pages,
  username,
  spaceSlug,
  currentSlug,
}: {
  pages: PageRow[];
  username: string;
  spaceSlug: string;
  currentSlug?: string;
}) {
  const tree = buildTree(pages);
  return (
    <nav className="p-1.5">
      <TreeNodes
        nodes={tree}
        username={username}
        spaceSlug={spaceSlug}
        currentSlug={currentSlug}
        depth={0}
      />
    </nav>
  );
}
